import { useMemo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Box, Alert, Button } from '@mui/material';
import { subDays, addDays, min as minDate, max as maxDate, format } from 'date-fns';
import { WorkshopCalendar } from '@/components/calendar/WorkshopCalendar';
import { useCalendarBookings, useCalendarAbsences, useUpdateBooking, type CalendarBooking } from '@/hooks/useBookings';
import { useMechanics } from '@/hooks/useStaffUsers';
import { useShiftSchedule } from '@/hooks/useShifts';
import { useTenantSettings } from '@/hooks/useTenantSettings';
import { useClockTimes } from '@/hooks/useTimeTracking';
import { useNotify } from '@/stores/notificationStore';
import { useCollectionSync } from '@/hooks/useEntitySync';
import { queryKeys } from '@/hooks/queryKeys';
import { buildCalendarSegments } from './calendarSegments';
import type { ServiceMove } from './calendarMerge';
import { describeApiError } from '@/lib/describeApiError';

interface WorkOrderCalendarTabProps {
  /** Fill the parent's available height (desktop full-bleed layout). */
  fillHeight?: boolean;
}

export function WorkOrderCalendarTab({ fillHeight = false }: WorkOrderCalendarTabProps) {
  const { t } = useTranslation('calendar');
  const { t: tc } = useTranslation('common');
  const queryClient = useQueryClient();
  const notify = useNotify();
  const updateBookingMutation = useUpdateBooking();

  // Real-time updates for calendar bookings
  useCollectionSync('bookings', {
    relevantScopes: ['status', 'details', 'services'],
    invalidationKeys: [
      { queryKey: queryKeys.bookings.all },
    ],
  });

  // Track the visible date range from WorkshopCalendar so the API query follows navigation
  const [visibleRange, setVisibleRange] = useState(() => {
    const today = new Date();
    return {
      start: subDays(today, 21),
      end: addDays(today, 21),
    };
  });

  const handleVisibleRangeChange = useCallback((start: Date, end: Date) => {
    // Add padding around visible range so data is ready when user scrolls
    const paddedStart = subDays(start, 14);
    const paddedEnd = addDays(end, 14);
    setVisibleRange((prev) => {
      // Only update if the new range extends beyond current range
      if (paddedStart >= prev.start && paddedEnd <= prev.end) return prev;
      // Always expand — never shrink — to prevent oscillation between views
      return {
        start: minDate([paddedStart, prev.start]),
        end: maxDate([paddedEnd, prev.end]),
      };
    });
  }, []);

  // Fetch calendar bookings from API — range follows user navigation
  const {
    data: calendarData,
    isPending: bookingsPending,
    isPlaceholderData: bookingsPlaceholder,
    isError,
    error,
    refetch,
  } = useCalendarBookings(
    visibleRange.start.toISOString(),
    visibleRange.end.toISOString()
  );

  const startStr = format(visibleRange.start, 'yyyy-MM-dd');
  const endStr = format(visibleRange.end, 'yyyy-MM-dd');

  const { data: shifts = [], isSuccess: shiftsReady } = useShiftSchedule({
    startDate: startStr,
    endDate: endStr,
  });

  const { data: clockTimes = [] } = useClockTimes(startStr, endStr);

  const { workdayUnpaidLunchMinutes } = useTenantSettings();

  // Compute renderable calendar segments — one item can yield multiple
  // segments when its duration spans shift boundaries. Unplaced floating
  // work is listed above the grid instead of occupying a synthesized slot.
  const { segments: serviceItems, unplaced: unplacedWork } = useMemo(() => {
    if (!calendarData?.data) return { segments: [], unplaced: [] };
    return buildCalendarSegments({
      bookings: calendarData.data,
      tentativeItems: calendarData.tentativeItems ?? [],
      shifts,
      unpaidLunchMinutes: workdayUnpaidLunchMinutes,
    });
  }, [calendarData, shifts, workdayUnpaidLunchMinutes]);

  const { data: mechanicUsers = [], isSuccess: mechanicsReady, isPending: mechanicsPending } = useMechanics();
  const { data: absences = [] } = useCalendarAbsences(
    visibleRange.start.toISOString(), visibleRange.end.toISOString(),
  );

  const mechanics = useMemo(() =>
    mechanicUsers.map((u) => ({
      id: u.id,
      firstName: u.firstName,
      lastName: u.lastName,
      email: u.email,
      phone: u.phone ?? '',
      skills: [] as string[],
      isAvailable: u.isActive,
    })),
    [mechanicUsers]
  );

  /** Booking-level scheduling type of a calendar item's parent booking. */
  const getBookingSchedulingType = useCallback(
    (bookingId: string): 'exact' | 'floating' => {
      const booking = calendarData?.data.find((b) => b.id === bookingId);
      return booking?.schedulingType ?? 'exact';
    },
    [calendarData],
  );

  const handleAssign = useCallback(
    async (
      serviceItemId: string, bookingId: string, mechanicId: string | null, newStart: Date,
      alsoMove: readonly ServiceMove[] = [],
    ) => {
      // The dropped job plus, for a merged entry, the jobs moving with it.
      const moves = [{ serviceItemId, newStart }, ...alsoMove];
      const movedIds = new Set(moves.map((move) => move.serviceItemId));
      const serviceMechanics: Record<string, string | null> = Object.fromEntries(
        moves.map((move) => [move.serviceItemId, mechanicId]),
      );

      // A row change keeps a floating booking floating (#1139): no snap time is
      // written — the backend re-lays the new mechanic's queue (#1137) and the
      // item lands at its computed position. Exact keeps today's behavior:
      // snap to the target mechanic's shift start.
      const isFloating = getBookingSchedulingType(bookingId) === 'floating';

      const serviceSchedules: Record<string, { scheduledAt: string }> = Object.fromEntries(
        moves.map((move) => [move.serviceItemId, { scheduledAt: move.newStart.toISOString() }]),
      );

      // Optimistic update — immediately move the service item in the cache
      const calendarQueryKey = queryKeys.bookings.calendar(
        visibleRange.start.toISOString(),
        visibleRange.end.toISOString()
      );
      const previousData = queryClient.getQueryData<{ data: CalendarBooking[] }>(calendarQueryKey);

      if (previousData) {
        const mechanic = mechanicId ? mechanicUsers.find((u) => u.id === mechanicId) : null;
        queryClient.setQueryData<{ data: CalendarBooking[] }>(calendarQueryKey, {
          ...previousData,
          data: previousData.data.map((b) =>
            b.id === bookingId
              ? {
                  ...b,
                  serviceAssignments: b.serviceAssignments.map((sa) =>
                    movedIds.has(sa.bookingServiceId)
                      ? {
                          ...sa,
                          assignedMechanicId: mechanicId,
                          mechanicName: mechanic ? `${mechanic.firstName} ${mechanic.lastName}` : null,
                          scheduledAt: serviceSchedules[sa.bookingServiceId]?.scheduledAt ?? sa.scheduledAt,
                        }
                      : sa
                  ),
                }
              : b
          ),
        });
      }

      try {
        await updateBookingMutation.mutateAsync({
          id: bookingId,
          data: isFloating
            ? {
                serviceMechanics,
                // Booking-level mechanic drives the floating-queue recompute
                // (#1137); omitting explicit times lets the backend place it.
                assignedMechanicId: mechanicId,
              }
            : {
                serviceMechanics,
                serviceSchedules,
              },
        });

        if (mechanicId) {
          const mechanic = mechanicUsers.find((u) => u.id === mechanicId);
          const name = mechanic ? `${mechanic.firstName} ${mechanic.lastName}` : '';
          notify.success(t('serviceAssigned'), t('serviceAssignedDesc', { mechanic: name }));
        } else {
          notify.success(t('serviceUnassigned'), t('serviceUnassignedDesc'));
        }
      } catch (err) {
        // Rollback optimistic update on failure
        if (previousData) {
          queryClient.setQueryData(calendarQueryKey, previousData);
        }
        notify.error(t('assignFailed'), describeApiError(err, tc, { fallback: tc('errors.generic') }));
      }
    },
    [updateBookingMutation, mechanicUsers, queryClient, visibleRange, notify, t, tc, getBookingSchedulingType]
  );

  const handleReschedule = useCallback(
    async (
      serviceItemId: string, bookingId: string, newStart: Date,
      alsoMove: readonly ServiceMove[] = [],
    ) => {
      // Check if this is a legacy item (no real bookingServiceId)
      const isLegacy = serviceItemId.startsWith('legacy-');
      // The dropped job plus, for a merged entry, the jobs moving with it.
      const serviceSchedules: Record<string, { scheduledAt: string }> = Object.fromEntries(
        [{ serviceItemId, newStart }, ...alsoMove]
          .map((move) => [move.serviceItemId, { scheduledAt: move.newStart.toISOString() }]),
      );

      // Dragging a floating booking to a time of day PINS it (#1139): it
      // becomes an exact appointment at the dropped time, and the snackbar
      // offers an undo that restores floating + the original day (the backend
      // then re-lays it into the queue, #1137).
      const booking = calendarData?.data.find((b) => b.id === bookingId);
      if (booking && (booking.schedulingType ?? 'exact') === 'floating') {
        const originalScheduledAt = booking.scheduledAt;
        try {
          await updateBookingMutation.mutateAsync({
            id: bookingId,
            data: {
              schedulingType: 'exact',
              scheduledAt: newStart.toISOString(),
            },
          });
          notify.success(t('floatingPinned'), t('floatingPinnedDesc'), {
            duration: 8000,
            action: {
              label: t('keepFloating'),
              onClick: () => {
                void (async () => {
                  try {
                    await updateBookingMutation.mutateAsync({
                      id: bookingId,
                      data: {
                        schedulingType: 'floating',
                        scheduledAt: originalScheduledAt,
                      },
                    });
                    notify.success(t('floatingRestored'), t('floatingRestoredDesc'));
                  } catch (err) {
                    notify.error(t('rescheduleFailed'), describeApiError(err, tc, { codePrefix: 'errorCode', fallback: tc('errors.generic') }));
                  }
                })();
              },
            },
          });
        } catch (err) {
          notify.error(t('rescheduleFailed'), describeApiError(err, tc, { codePrefix: 'errorCode', fallback: tc('errors.generic') }));
        }
        return;
      }

      // Optimistic update — the dropped item stays where it was dropped
      // instead of jumping back until the refetch lands.
      const calendarQueryKey = queryKeys.bookings.calendar(
        visibleRange.start.toISOString(),
        visibleRange.end.toISOString()
      );
      const previousData = queryClient.getQueryData<{ data: CalendarBooking[] }>(calendarQueryKey);
      if (previousData) {
        const scheduledAt = newStart.toISOString();
        queryClient.setQueryData<{ data: CalendarBooking[] }>(calendarQueryKey, {
          ...previousData,
          data: previousData.data.map((b) => {
            if (b.id !== bookingId) return b;
            if (isLegacy) return { ...b, scheduledAt };
            return {
              ...b,
              serviceAssignments: b.serviceAssignments.map((sa) => {
                const moved = serviceSchedules[sa.bookingServiceId];
                return moved ? { ...sa, scheduledAt: moved.scheduledAt } : sa;
              }),
            };
          }),
        });
      }

      try {
        if (isLegacy) {
          // Legacy: update booking-level scheduledAt
          await updateBookingMutation.mutateAsync({
            id: bookingId,
            data: {
              scheduledAt: newStart.toISOString(),
            },
          });
        } else {
          // Per-service: update only the moved services' scheduledAt
          await updateBookingMutation.mutateAsync({
            id: bookingId,
            data: { serviceSchedules },
          });
        }
        notify.success(t('serviceMoved'), t('serviceMovedDesc'));
      } catch (err) {
        // Revert the drop. A lift clash (#2842) answers 409 with
        // code lift_unavailable, translated under common:errorCode.
        if (previousData) {
          queryClient.setQueryData(calendarQueryKey, previousData);
        }
        notify.error(t('rescheduleFailed'), describeApiError(err, tc, { codePrefix: 'errorCode', fallback: tc('errors.generic') }));
      }
    },
    [updateBookingMutation, calendarData, queryClient, visibleRange, notify, t, tc]
  );

  // Error state — only when no data available at all
  if (isError && !calendarData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void refetch()}>
              {tc('errors.refresh')}
            </Button>
          }
        >
          {t('errorLoading')}: {describeApiError(error, tc, { fallback: tc('errors.generic') })}
        </Alert>
      </Box>
    );
  }

  return (
    <WorkshopCalendar
      serviceItems={serviceItems}
      unplacedWork={unplacedWork}
      mechanics={mechanics}
      shifts={shifts}
      clockTimes={clockTimes}
      absences={absences}
      utilizationReady={shiftsReady && mechanicsReady}
      loading={bookingsPending || bookingsPlaceholder || mechanicsPending}
      onReschedule={handleReschedule}
      onAssign={handleAssign}
      onVisibleRangeChange={handleVisibleRangeChange}
      fillHeight={fillHeight}
    />
  );
}
